import type { ReactNode } from 'react';

import { cn } from '@/lib/utils';


interface GradientBackgroundProps {
  children: ReactNode;
  className?: string;
  contentClassName?: string;
}

const STANDARD_GRADIENT_BLOBS = {
  topLeft: {
    bg: 'radial-gradient(circle, hsl(235 85% 45%) 0%, hsl(230 65% 48% / 0.35) 25%, hsl(226 45% 52% / 0.08) 45%, transparent 60%)',
    size: 'h-[650px] w-[650px]',
    pos: '-left-48 -top-48',
    opacity: 'opacity-[0.20] dark:opacity-[0.22]',
    blur: 'blur(120px)',
  },
  topRight: {
    bg: 'radial-gradient(circle, hsl(238 75% 48%) 0%, hsl(232 55% 50% / 0.25) 30%, transparent 55%)',
    size: 'h-[450px] w-[450px]',
    pos: '-right-32 -top-24',
    opacity: 'opacity-[0.12] dark:opacity-[0.16]',
    blur: 'blur(130px)',
  },
  bottomCenter: {
    bg: 'radial-gradient(circle, hsl(50 90% 68%) 0%, hsl(46 75% 70% / 0.45) 22%, hsl(42 55% 75% / 0.15) 40%, transparent 62%)',
    size: 'h-[500px] w-[650px]',
    pos: '-bottom-12 left-[58%] -translate-x-1/2',
    opacity: 'opacity-[0.22] dark:opacity-[0.10]',
    blur: 'blur(100px)',
  },
} as const;

export function GradientBackground({
  children,
  className,
  contentClassName,
}: GradientBackgroundProps) {
  const blobs = STANDARD_GRADIENT_BLOBS;

  return (
    <div
      className={cn(
        'app-gradient-background font-activation relative overflow-hidden',
        className
      )}
    >
      {/* Layer 3: Blobs — z-2 */}
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute z-[2] rounded-full',
          blobs.topLeft.size,
          blobs.topLeft.pos,
          blobs.topLeft.opacity
        )}
        style={{ background: blobs.topLeft.bg, filter: blobs.topLeft.blur }}
      />
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute z-[2] rounded-full',
          blobs.topRight.size,
          blobs.topRight.pos,
          blobs.topRight.opacity
        )}
        style={{ background: blobs.topRight.bg, filter: blobs.topRight.blur }}
      />
      <div
        aria-hidden="true"
        className={cn(
          'pointer-events-none absolute z-[2] rounded-full',
          blobs.bottomCenter.size,
          blobs.bottomCenter.pos,
          blobs.bottomCenter.opacity
        )}
        style={{ background: blobs.bottomCenter.bg, filter: blobs.bottomCenter.blur }}
      />
      {/* Dot grid — z-5, above blobs, below content */}
      <div
        aria-hidden="true"
        className="hero-dot-grid pointer-events-none absolute inset-0 z-[5]"
      />
      <div className={cn('relative z-10', contentClassName)}>{children}</div>
    </div>
  );
}
